// ============================================
// Landing Page - PokerMassage
// ============================================

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../config/firebase';
import './Landing.css';

// Icons as simple SVG components
const IconLive = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/>
    <polyline points="12,6 12,12 16,14"/>
  </svg>
);

const IconWalk = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="5" r="2"/>
    <path d="M10 22V18L7 15L10 12V8"/>
    <path d="M14 22V18L17 15L14 12V8"/>
  </svg>
);

const IconQR = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="7" height="7"/>
    <rect x="14" y="3" width="7" height="7"/>
    <rect x="3" y="14" width="7" height="7"/>
    <rect x="14" y="14" width="3" height="3"/>
    <rect x="18" y="14" width="3" height="3"/>
    <rect x="14" y="18" width="3" height="3"/>
    <rect x="18" y="18" width="3" height="3"/>
  </svg>
);

const IconChart = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="20" x2="18" y2="10"/>
    <line x1="12" y1="20" x2="12" y2="4"/>
    <line x1="6" y1="20" x2="6" y2="14"/>
  </svg>
);

const IconCheck = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20,6 9,17 4,12"/>
  </svg>
);

const IconBuilding = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="4" y="2" width="16" height="20" rx="2" ry="2"/>
    <path d="M9 22v-4h6v4"/>
    <line x1="8" y1="6" x2="8" y2="6"/>
    <line x1="12" y1="6" x2="12" y2="6"/>
    <line x1="16" y1="6" x2="16" y2="6"/>
    <line x1="8" y1="10" x2="8" y2="10"/>
    <line x1="12" y1="10" x2="12" y2="10"/>
    <line x1="16" y1="10" x2="16" y2="10"/>
    <line x1="8" y1="14" x2="8" y2="14"/>
    <line x1="12" y1="14" x2="12" y2="14"/>
    <line x1="16" y1="14" x2="16" y2="14"/>
  </svg>
);

const IconCards = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="4" width="14" height="16" rx="2"/>
    <path d="M22 8v8a2 2 0 01-2 2h-2"/>
    <path d="M18 4h2a2 2 0 012 2v2"/>
  </svg>
);

const IconUsers = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/>
    <circle cx="9" cy="7" r="4"/>
    <path d="M23 21v-2a4 4 0 00-3-3.87"/>
    <path d="M16 3.13a4 4 0 010 7.75"/>
  </svg>
);

const IconChevronDown = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="6,9 12,15 18,9"/>
  </svg>
);

interface FAQItemProps {
  question: string;
  answer: string;
}

function FAQItem({ question, answer }: FAQItemProps) {
  const [isOpen, setIsOpen] = React.useState(false);

  return (
    <div className={`faq-item ${isOpen ? 'open' : ''}`}>
      <button className="faq-question" onClick={() => setIsOpen(!isOpen)}>
        <span>{question}</span>
        <span className="faq-icon"><IconChevronDown /></span>
      </button>
      <div className="faq-answer">
        <p>{answer}</p>
      </div>
    </div>
  );
}

export default function Landing() {
  const navigate = useNavigate();
  const [showModal, setShowModal] = React.useState(false);
  const [formData, setFormData] = React.useState({
    name: '',
    email: '',
    phone: '',
    organization: '',
    role: '',
    message: '',
  });
  const [submitting, setSubmitting] = React.useState(false);
  const [submitted, setSubmitted] = React.useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);

    // Track sign up submit event
    if (typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('event', 'sign_up_submit', {
        event_category: 'engagement',
        event_label: formData.role,
      });
    }

    try {
      // Save to Firestore
      await addDoc(collection(db, 'signups'), {
        name: formData.name,
        email: formData.email,
        phone: formData.phone || null,
        organization: formData.organization,
        role: formData.role,
        message: formData.message || null,
        createdAt: serverTimestamp(),
        status: 'new',
      });

      setSubmitting(false);
      setSubmitted(true);
    } catch (err) {
      console.error('Error saving signup:', err);
      setSubmitting(false);
      alert('Something went wrong. Please try again.');
    }
  };

  const openSignUp = () => {
    // Track sign up click event
    if (typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('event', 'sign_up_click', {
        event_category: 'engagement',
      });
    }
    setShowModal(true);
  };

  const handleLoginClick = () => {
    // Track login click event
    if (typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('event', 'login_click', {
        event_category: 'engagement',
      });
    }
    navigate('/login');
  };

  const faqs = [
    {
      question: 'Do therapists need to follow a schedule?',
      answer: 'No. Therapists work freely on the floor and track sessions in real time.',
    },
    {
      question: 'Do we have to use QR booking?',
      answer: 'No. QR is optional. The app is valuable even if you only use it for tracking.',
    },
    {
      question: 'What happens when a player requests a session?',
      answer: 'The player requests a session from an available therapist, and the therapist accepts or declines. Nothing is auto-assigned.',
    },
    {
      question: 'Do players need to download anything?',
      answer: 'No. It works in the browser on any phone.',
    },
    {
      question: 'Can it handle multi-day festivals?',
      answer: 'Yes. It is designed for long weekends and full series.',
    },
    {
      question: 'Does the app handle payments?',
      answer: 'No. It focuses on tracking and flow during the event, so payments can stay with your preferred setup.',
    },
  ];

  return (
    <div className="landing-page">
      {/* Header */}
      <header className="landing-header">
        <div className="landing-container">
          <div className="landing-logo">PokerMassage</div>
          <nav className="landing-nav">
            <button onClick={handleLoginClick} className="nav-link">Login</button>
          </nav>
        </div>
      </header>

      {/* Hero Section */}
      <section className="hero-section">
        <div className="landing-container">
          <h1>Massage tracking for poker events, without the chaos.</h1>
          <p className="hero-intro">
            PokerMassage is a simple web app that helps your massage team track sessions in real time at poker events.
            Therapists work like normal by walking the room, and log each session as they go. If you choose to use QR booking,
            players can see who is available and request a session in seconds, and the therapist accepts it.
          </p>
          <p className="keyword-line">
            PokerMassage is a web app for poker tournament massage tracking with optional QR booking requests.
          </p>
          <div className="hero-cta">
            <button onClick={openSignUp} className="btn-primary btn-large">Sign Up</button>
            <span className="cta-note">We'll contact you shortly.</span>
          </div>
        </div>
      </section>

      {/* How Therapists Work */}
      <section className="feature-section">
        <div className="landing-container">
          <h2>Built for how massage teams actually work</h2>
          <p className="section-intro">
            No fixed schedule to babysit. Therapists stay mobile, take sessions naturally on the floor,
            and simply track what they do in the app. You get clean ops and better overview without slowing anyone down.
          </p>
          <div className="feature-visual">
            <div className="mock-ui">
              <div className="mock-header">
                <span className="mock-status available">Available</span>
              </div>
              <div className="mock-session-list">
                <div className="mock-session">
                  <span className="mock-table">Table 7, Seat 3</span>
                  <span className="mock-time">30 min</span>
                  <span className="mock-badge active">Active</span>
                </div>
                <div className="mock-session completed">
                  <span className="mock-table">Table 4, Seat 8</span>
                  <span className="mock-time">20 min</span>
                  <span className="mock-badge done">Done</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* QR Booking */}
      <section className="feature-section alt">
        <div className="landing-container">
          <h2>Optional QR booking, not a must</h2>
          <p className="section-intro">
            When enabled, players scan a QR code, see who is available, and request a session.
            The therapist stays in control and accepts or declines. If you do not use QR, nothing changes,
            the app still works perfectly for tracking.
          </p>
          <div className="feature-visual">
            <div className="mock-ui player-view">
              <div className="mock-therapist-card">
                <div className="mock-avatar">M</div>
                <div className="mock-info">
                  <span className="mock-name">Maria</span>
                  <span className="mock-availability">Available now</span>
                </div>
                <button className="mock-btn">Request</button>
              </div>
              <div className="mock-therapist-card">
                <div className="mock-avatar">J</div>
                <div className="mock-info">
                  <span className="mock-name">James</span>
                  <span className="mock-availability busy">In session</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="benefits-section">
        <div className="landing-container">
          <h2>Why organizers and massage teams love it</h2>
          <div className="benefits-grid">
            <div className="benefit-item">
              <span className="benefit-icon"><IconLive /></span>
              <span className="benefit-text">Track sessions live, therapist by therapist</span>
            </div>
            <div className="benefit-item">
              <span className="benefit-icon"><IconWalk /></span>
              <span className="benefit-text">Keep the walk-the-room workflow</span>
            </div>
            <div className="benefit-item">
              <span className="benefit-icon"><IconQR /></span>
              <span className="benefit-text">Optional QR requests with therapist approval</span>
            </div>
            <div className="benefit-item">
              <span className="benefit-icon"><IconChart /></span>
              <span className="benefit-text">Better utilization and fewer missed opportunities</span>
            </div>
            <div className="benefit-item">
              <span className="benefit-icon"><IconCheck /></span>
              <span className="benefit-text">Clean reporting after the event</span>
            </div>
          </div>
        </div>
      </section>

      {/* Who It's For */}
      <section className="audience-section">
        <div className="landing-container">
          <h2>Who it's for</h2>
          <div className="audience-grid">
            <div className="audience-item">
              <span className="audience-icon"><IconCards /></span>
              <span className="audience-title">Tournament organizers</span>
            </div>
            <div className="audience-item">
              <span className="audience-icon"><IconBuilding /></span>
              <span className="audience-title">Cardrooms and casinos</span>
            </div>
            <div className="audience-item">
              <span className="audience-icon"><IconUsers /></span>
              <span className="audience-title">On-site massage teams</span>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="how-it-works-section">
        <div className="landing-container">
          <h2>How it works</h2>
          <div className="steps-list">
            <div className="step-item">
              <span className="step-number">1</span>
              <span className="step-text">Therapists walk the room and take sessions as normal</span>
            </div>
            <div className="step-item">
              <span className="step-number">2</span>
              <span className="step-text">They log sessions in the app as they happen</span>
            </div>
            <div className="step-item">
              <span className="step-number">3</span>
              <span className="step-text">Optional: players scan a QR code to request a session from available therapists</span>
            </div>
            <div className="step-item">
              <span className="step-number">4</span>
              <span className="step-text">The therapist accepts the request and the session is tracked</span>
            </div>
            <div className="step-item">
              <span className="step-number">5</span>
              <span className="step-text">After the event you have clear numbers and overview</span>
            </div>
          </div>
          <div className="section-cta">
            <button onClick={openSignUp} className="btn-primary btn-large">Sign Up</button>
            <span className="cta-note">We'll contact you shortly.</span>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="faq-section">
        <div className="landing-container">
          <h2>Frequently asked questions</h2>
          <div className="faq-list">
            {faqs.map((faq, index) => (
              <FAQItem key={index} question={faq.question} answer={faq.answer} />
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="final-cta-section">
        <div className="landing-container">
          <h2>Track more sessions. Run smoother events.</h2>
          <div className="section-cta">
            <button onClick={openSignUp} className="btn-primary btn-large">Sign Up</button>
            <span className="cta-note">We'll contact you shortly.</span>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="landing-footer">
        <div className="landing-container">
          <div className="footer-content">
            <div className="footer-brand">PokerMassage</div>
            <div className="footer-links">
              <button onClick={handleLoginClick} className="footer-link">Admin login</button>
              <button onClick={() => navigate('/t')} className="footer-link">Therapist login</button>
            </div>
          </div>
        </div>
      </footer>

      {/* Sign Up Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => !submitting && setShowModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            {submitted ? (
              <div className="modal-success">
                <div className="success-icon">
                  <IconCheck />
                </div>
                <h3>Thank you!</h3>
                <p>We'll contact you shortly to discuss how PokerMassage can help your events.</p>
                <button onClick={() => { setShowModal(false); setSubmitted(false); }} className="btn-primary">
                  Close
                </button>
              </div>
            ) : (
              <>
                <button className="modal-close" onClick={() => setShowModal(false)}>&times;</button>
                <h3>Get started with PokerMassage</h3>
                <p>Tell us about your event and we'll reach out to help you get set up.</p>
                <form onSubmit={handleSubmit} className="signup-form">
                  <div className="form-group">
                    <label htmlFor="name">Name *</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="email">Email *</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="phone">Phone</label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="organization">Organization / Event *</label>
                    <input
                      type="text"
                      id="organization"
                      name="organization"
                      value={formData.organization}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="role">Your role *</label>
                    <select
                      id="role"
                      name="role"
                      value={formData.role}
                      onChange={handleChange}
                      required
                    >
                      <option value="">Select...</option>
                      <option value="organizer">Tournament organizer</option>
                      <option value="massage_team">Massage team</option>
                      <option value="venue">Venue / Casino</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label htmlFor="message">Message (optional)</label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      rows={3}
                    />
                  </div>
                  <button type="submit" className="btn-primary btn-large" disabled={submitting}>
                    {submitting ? 'Sending...' : 'Submit'}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
